/**
 * Integration context validation (ENG-4341).
 *
 * Two layers of validation:
 *
 * 1. **Meta-schema validation** (`validateContextSchema`)
 *    Run when a plugin author saves their plugin's `context_schema`. Ensures
 *    the schema only uses the constrained subset of JSON Schema we support
 *    (string / boolean / string[] / string-keyed map). Rejects unsupported
 *    keywords like `oneOf`, `$ref`, `number`, nested objects, etc.
 *
 * 2. **Values validation** (`validateContextValues`)
 *    Run on `PUT /plugins/:id/context` to verify user-submitted values
 *    actually match the plugin's declared schema. Compiles the plugin's
 *    `context_schema` with Ajv and validates the values against it.
 *    Compiled schemas are cached by reference for performance.
 *
 * Both functions return `{ valid, data, errors }` mirroring the existing
 * charter/tools validators in `packages/core/src/schemas/validators.ts`.
 */

import Ajv2020 from 'ajv/dist/2020.js';
import addFormats from 'ajv-formats';
import metaSchema from './context-meta-schema.json' with { type: 'json' };
import type {
  IntegrationContextSchema,
  IntegrationContextValues,
} from '../types/integration.js';

const ajv = new Ajv2020({ allErrors: true, strict: false });
addFormats(ajv);

const compiledMetaSchema = ajv.compile<IntegrationContextSchema>(metaSchema);

export interface IntegrationContextValidationError {
  path: string;
  message: string;
}

export interface IntegrationContextValidationResult<T> {
  valid: boolean;
  data?: T;
  errors: IntegrationContextValidationError[];
}

function formatErrors(
  errors: typeof compiledMetaSchema.errors,
): IntegrationContextValidationError[] {
  if (!errors) return [];
  return errors.map((e) => ({
    path: e.instancePath || '/',
    message: e.message ?? 'Unknown validation error',
  }));
}

/**
 * Validate a plugin's `context_schema` against the meta-schema for the
 * supported JSON Schema subset. Call this when a plugin author saves a
 * plugin definition that includes a `context_schema`.
 */
export function validateContextSchema(
  data: unknown,
): IntegrationContextValidationResult<IntegrationContextSchema> {
  const valid = compiledMetaSchema(data);
  return {
    valid,
    data: valid ? (data as IntegrationContextSchema) : undefined,
    errors: formatErrors(compiledMetaSchema.errors),
  };
}

// ---------------------------------------------------------------------------
// Compiled-schema cache for value validation
// ---------------------------------------------------------------------------
//
// Compiling a JSON Schema with Ajv is non-trivial work and we may validate
// many context PUTs against the same schema in succession. Cache compiled
// validators by the schema's identity (WeakMap keyed by the schema object).
// Callers that need referential stability should pass the same object each
// time; callers that fetch the schema fresh from the DB will pay the
// compile cost once per request, which is fine.

const compiledSchemaCache = new WeakMap<
  IntegrationContextSchema,
  ReturnType<typeof ajv.compile>
>();

function compileForSchema(
  schema: IntegrationContextSchema,
): ReturnType<typeof ajv.compile> {
  const cached = compiledSchemaCache.get(schema);
  if (cached) return cached;
  const compiled = ajv.compile(schema);
  compiledSchemaCache.set(schema, compiled);
  return compiled;
}

/**
 * Validate user-submitted plugin context values against the plugin's
 * declared `context_schema`. Use this on `PUT /plugins/:id/context` before
 * persisting `plugin_context.values`.
 *
 * The schema MUST already have passed `validateContextSchema` — this
 * function trusts that the schema is well-formed and only checks values
 * against it.
 */
export function validateContextValues(
  schema: IntegrationContextSchema,
  values: unknown,
): IntegrationContextValidationResult<IntegrationContextValues> {
  const compiled = compileForSchema(schema);
  // Ajv compile returns `boolean | Promise<unknown>` because async schemas
  // exist; ours never are, so coerce the result.
  const valid = compiled(values) === true;
  return {
    valid,
    data: valid ? (values as IntegrationContextValues) : undefined,
    errors: formatErrors(compiled.errors),
  };
}

/**
 * Apply schema defaults to a values object, returning a new object where
 * any field declared in the schema with a `default` and missing from the
 * input gets the default value. Pure function — does not mutate input.
 *
 * Used by `/host/refresh` to deliver pre-resolved context to the manager
 * so the substitution layer never has to think about defaults.
 */
export function applyContextDefaults(
  schema: IntegrationContextSchema | null,
  values: IntegrationContextValues,
): IntegrationContextValues {
  if (!schema?.properties) return { ...values };
  const result: IntegrationContextValues = { ...values };
  for (const [key, field] of Object.entries(schema.properties)) {
    if (key in result) continue;
    if (field.default !== undefined) {
      result[key] = field.default;
    }
  }
  return result;
}
